// 🔥 용량/연결성 관리 전용 Drawer 컴포넌트 (개선 버전)
// [배경] 기존 모델에 새 용량(Storage) 및 연결성(Connectivity)을 추가/삭제하는 전용 UI
//        "신규 모델 추가"와 명확히 분리된 UX
//        태블릿/스마트폰/웨어러블 공통 사용
// [버그 수정] Wi-Fi/Cellular 개별 추가 기능 추가

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Chip,
  Alert,
  AlertTitle,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Tooltip,
  Grid,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Restore as RestoreIcon,
  Save as SaveIcon,
  Storage as StorageIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
  Wifi as WifiIcon,
  SignalCellularAlt as CellularIcon,
  GridOn as GridIcon
} from '@mui/icons-material';

import { DeviceCatalogDocument, generatePriceId, ConnectivityType } from '../../../../types/deviceCatalog';
import { DeviceGrade, GradePriceInfo } from '../../../../types/gradeSystem';
import { getGradesForCategory } from '../../../../constants/grades';
import { DeviceCatalogService } from '../../../../services/deviceCatalogService';
import { getUnifiedPriceService } from '../../../../services/unifiedPriceAdapter';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../../../firebase';

// 📌 Props 인터페이스
export interface VariantManagerDrawerProps {
  /** 열림 상태 */
  open: boolean;
  /** 닫기 핸들러 */
  onClose: () => void;
  /** 대상 모델 */
  model: DeviceCatalogDocument | null;
  /** 디바이스 타입 */
  deviceType: 'tablet' | 'smartphone' | 'wearable';
  /** 브랜드 */
  brand: 'Apple' | 'Samsung';
  /** 성공 콜백 */
  onSuccess: () => void;
}

// 📌 새로 추가할 용량 정보
interface NewStorageItem {
  storage: string;
  initialPrices: GradePriceInfo;
}

// 📌 새로 추가할 연결성 정보
interface NewConnectivityItem {
  connectivity: string;
  storages: string[]; // 해당 연결성에 추가할 용량들
  initialPrices: GradePriceInfo;
}

// 📌 개별 Variant (용량+연결성 조합)
interface VariantCombination {
  storage: string;
  connectivity: string;
  isActive: boolean; // 활성 상태
  isNew?: boolean; // 새로 추가 예정
  toDelete?: boolean; // 삭제 예정
}


// 🔌 연결성 옵션 목록
const CONNECTIVITY_OPTIONS = ['Wi-Fi', 'Wi-Fi + Cellular', 'Wi-Fi + 5G'];

/**
 * 🔥 용량 관리 전용 Drawer
 * 
 * @description
 * 기존 모델에 새 용량을 추가/삭제하는 전용 UI입니다.
 * - 현재 등록된 용량 목록 표시
 * - 새 용량 추가 (초기 가격 설정 가능)
 * - 기존 용량 삭제 (soft delete - disabledVariants에 추가)
 */
const VariantManagerDrawer: React.FC<VariantManagerDrawerProps> = ({
  open,
  onClose,
  model,
  deviceType,
  brand,
  onSuccess
}) => {
  // 📊 상태 관리
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // 📊 탭 상태 (0: 용량관리, 1: 연결성관리, 2: 조합관리)
  const [activeTab, setActiveTab] = useState(0);

  // 📊 새 용량 추가 상태
  const [newStorages, setNewStorages] = useState<NewStorageItem[]>([]);
  const [selectedStorages, setSelectedStorages] = useState<Set<string>>(new Set()); // 체크박스 선택된 용량들
  const [customStorageInput, setCustomStorageInput] = useState(''); // 직접 입력 용량
  const [newStoragePrices, setNewStoragePrices] = useState<GradePriceInfo>({ A: 0, B: 0, C: 0, D: 0, E: 0 });

  // 📊 삭제 예정 용량 상태
  const [storagesToDelete, setStoragesToDelete] = useState<string[]>([]);
  
  // 🔌 새 연결성 추가 상태 (버그 수정)
  const [newConnectivities, setNewConnectivities] = useState<NewConnectivityItem[]>([]);
  const [selectedConnectivity, setSelectedConnectivity] = useState<string>(''); // 추가할 연결성
  const [connectivityStorages, setConnectivityStorages] = useState<Set<string>>(new Set()); // 연결성에 추가할 용량들
  const [newConnectivityPrices, setNewConnectivityPrices] = useState<GradePriceInfo>({ A: 0, B: 0, C: 0, D: 0, E: 0 });
  
  // 📊 개별 조합 삭제 상태
  const [variantsToDelete, setVariantsToDelete] = useState<Set<string>>(new Set()); // "storage-connectivity" 형식

  // 🔄 모델 변경 시 상태 초기화
  useEffect(() => {
    if (model && open) {
      setNewStorages([]);
      setSelectedStorages(new Set());
      setCustomStorageInput('');
      setNewStoragePrices({ A: 0, B: 0, C: 0, D: 0, E: 0 });
      setStoragesToDelete([]);
      // 🔌 연결성 상태 초기화
      setNewConnectivities([]);
      setSelectedConnectivity('');
      setConnectivityStorages(new Set());
      setNewConnectivityPrices({ A: 0, B: 0, C: 0, D: 0, E: 0 });
      setVariantsToDelete(new Set());
      setActiveTab(0);
      setError(null);
      setSuccessMessage(null);
    }
  }, [model, open]);

  // 📊 현재 활성 용량 목록 (모든 연결성이 비활성화된 용량만 제외)
  // 🔥 버그 수정: 특정 용량의 "모든" 연결성이 비활성화된 경우에만 해당 용량을 비활성으로 간주
  const activeStorageOptions = useMemo(() => {
    if (!model) return [];
    
    const allStorages = model.storageOptions || [];
    const allConnectivities = model.connectivityOptions || ['Wi-Fi'];
    const disabledVariants: { storage: string; connectivity?: string }[] = 
      (model as any).disabledVariants || [];
    
    // 각 용량에 대해, 해당 용량의 모든 연결성이 비활성화되었는지 확인
    return allStorages.filter(storage => {
      // 이 용량의 모든 연결성이 비활성화되었는지 확인
      const allConnectivitiesDisabled = allConnectivities.every(connectivity => 
        disabledVariants.some(v => 
          v.storage === storage && (v.connectivity || 'Wi-Fi') === connectivity
        )
      );
      // 하나라도 활성 연결성이 있으면 용량은 활성
      return !allConnectivitiesDisabled;
    });
  }, [model]);
  
  // 🔌 현재 활성 연결성 목록
  const activeConnectivityOptions = useMemo(() => {
    if (!model) return [];
    return model.connectivityOptions || ['Wi-Fi'];
  }, [model]);
  
  // 📊 현재 활성화된 용량×연결성 조합 목록
  const activeVariantCombinations = useMemo<VariantCombination[]>(() => {
    if (!model) return [];
    
    const disabledVariants: { storage: string; connectivity?: string }[] = 
      (model as any).disabledVariants || [];
    
    const combinations: VariantCombination[] = [];
    const storages = model.storageOptions || [];
    const connectivities = model.connectivityOptions || ['Wi-Fi'];
    
    storages.forEach(storage => {
      connectivities.forEach(connectivity => {
        const isDisabled = disabledVariants.some(
          v => v.storage === storage && (v.connectivity || 'Wi-Fi') === connectivity
        );
        combinations.push({
          storage,
          connectivity,
          isActive: !isDisabled
        });
      });
    });
    
    return combinations;
  }, [model]);
  
  // 🔌 추가 가능한 연결성 옵션 (현재 없는 것만)
  const availableConnectivityOptions = useMemo(() => {
    const existing = activeConnectivityOptions;
    const newlyAdded = newConnectivities.map(nc => nc.connectivity);
    return CONNECTIVITY_OPTIONS.filter(opt => !existing.includes(opt) && !newlyAdded.includes(opt));
  }, [activeConnectivityOptions, newConnectivities]);

  // 📊 기본 스토리지 옵션 (추가 시 선택지)
  const getDefaultStorageOptions = () => {
    if (deviceType === 'wearable') {
      return ['풀세트', '유닛 세트', '유닛 1쪽', '본체만'];
    }
    return ['32GB', '64GB', '128GB', '256GB', '512GB', '1TB', '2TB'];
  };

  // 📊 등급 명칭 반환
  const getGradeLabel = (grade: DeviceGrade): string => {
    return `${grade}등급`;
  };
  
  // 🔌 연결성 아이콘 반환
  const getConnectivityIcon = (connectivity: string) => {
    if (connectivity.includes('Cellular') || connectivity.includes('5G') || connectivity.includes('LTE')) {
      return <CellularIcon fontSize="small" />;
    }
    return <WifiIcon fontSize="small" />;
  };

  // ☑️ 체크박스 선택 토글 핸들러
  const handleStorageCheckChange = useCallback((storage: string, checked: boolean) => {
    setSelectedStorages(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(storage);
      } else {
        newSet.delete(storage);
      }
      return newSet;
    });
  }, []);

  // ➕ 선택된 용량들 일괄 추가 핸들러
  const handleAddSelectedStorages = useCallback(() => {
    if (selectedStorages.size === 0) {
      setError('추가할 용량을 선택해주세요.');
      return;
    }

    const allExistingStorages = [
      ...activeStorageOptions.filter(s => !storagesToDelete.includes(s)),
      ...newStorages.map(ns => ns.storage)
    ];

    const storagesToAdd: NewStorageItem[] = [];
    const restoredStorages: string[] = [];
    const duplicateStorages: string[] = [];

    Array.from(selectedStorages).forEach(storage => {
      // 중복 체크
      if (allExistingStorages.includes(storage)) {
        duplicateStorages.push(storage);
        return;
      }

      // 삭제 예정 목록에서 복원
      if (storagesToDelete.includes(storage)) {
        restoredStorages.push(storage);
        return;
      }

      storagesToAdd.push({
        storage,
        initialPrices: { ...newStoragePrices }
      });
    });

    // 복원 처리
    if (restoredStorages.length > 0) {
      setStoragesToDelete(prev => prev.filter(s => !restoredStorages.includes(s)));
    }

    // 새 용량 추가
    if (storagesToAdd.length > 0) {
      setNewStorages(prev => [...prev, ...storagesToAdd]);
    }

    // 선택 초기화
    setSelectedStorages(new Set());
    setNewStoragePrices({ A: 0, B: 0, C: 0, D: 0, E: 0 });
    setError(null);

    // 결과 메시지
    const messages: string[] = [];
    if (storagesToAdd.length > 0) {
      messages.push(`${storagesToAdd.map(s => s.storage).join(', ')} 추가됨`);
    }
    if (restoredStorages.length > 0) {
      messages.push(`${restoredStorages.join(', ')} 복원됨`);
    }
    if (duplicateStorages.length > 0) {
      messages.push(`${duplicateStorages.join(', ')} 이미 존재`);
    }
    if (messages.length > 0) {
      setSuccessMessage(messages.join(' / '));
    }
  }, [selectedStorages, newStoragePrices, activeStorageOptions, newStorages, storagesToDelete]);

  // ➕ 직접 입력 용량 추가 핸들러
  const handleAddCustomStorage = useCallback(() => {
    const trimmedStorage = customStorageInput.trim();
    
    if (!trimmedStorage) {
      setError('용량을 입력해주세요.');
      return;
    }
    
    const allExistingStorages = [
      ...activeStorageOptions.filter(s => !storagesToDelete.includes(s)),
      ...newStorages.map(ns => ns.storage)
    ];
    
    if (allExistingStorages.includes(trimmedStorage)) {
      setError(`"${trimmedStorage}" 용량은 이미 존재합니다.`);
      return;
    }
    
    if (storagesToDelete.includes(trimmedStorage)) {
      setStoragesToDelete(prev => prev.filter(s => s !== trimmedStorage));
      setCustomStorageInput('');
      setSuccessMessage(`"${trimmedStorage}" 용량이 복원되었습니다.`);
      return;
    }
    
    setNewStorages(prev => [...prev, {
      storage: trimmedStorage,
      initialPrices: { ...newStoragePrices }
    }]);
    
    setCustomStorageInput('');
    setError(null);
    setSuccessMessage(`"${trimmedStorage}" 용량이 추가되었습니다.`);
  }, [customStorageInput, newStoragePrices, activeStorageOptions, newStorages, storagesToDelete]);

  // ❌ 새 용량 제거 (추가 대기 목록에서)
  const handleRemoveNewStorage = useCallback((storage: string) => {
    setNewStorages(prev => prev.filter(ns => ns.storage !== storage));
  }, []);

  // 🗑️ 기존 용량 삭제 예정 토글
  const handleToggleDeleteStorage = useCallback((storage: string) => {
    if (storagesToDelete.includes(storage)) {
      // 삭제 취소 (복원)
      setStoragesToDelete(prev => prev.filter(s => s !== storage));
    } else {
      // 삭제 예정에 추가
      setStoragesToDelete(prev => [...prev, storage]);
    }
  }, [storagesToDelete]);
  
  // 🔌 연결성 용량 체크박스 토글
  const handleConnectivityStorageToggle = useCallback((storage: string, checked: boolean) => {
    setConnectivityStorages(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(storage);
      } else {
        newSet.delete(storage);
      }
      return newSet;
    });
  }, []);
  
  // 🔌 새 연결성 추가 핸들러
  const handleAddConnectivity = useCallback(() => {
    if (!selectedConnectivity) {
      setError('추가할 연결성을 선택해주세요.');
      return;
    }
    
    if (connectivityStorages.size === 0) {
      setError('연결성을 추가할 용량을 최소 1개 선택해주세요.');
      return;
    }
    
    // 이미 추가 대기 중인지 확인
    if (newConnectivities.some(nc => nc.connectivity === selectedConnectivity)) {
      setError(`"${selectedConnectivity}" 연결성은 이미 추가 대기 중입니다.`);
      return;
    }
    
    setNewConnectivities(prev => [...prev, {
      connectivity: selectedConnectivity,
      storages: Array.from(connectivityStorages),
      initialPrices: { ...newConnectivityPrices }
    }]);
    
    setSelectedConnectivity('');
    setConnectivityStorages(new Set());
    setNewConnectivityPrices({ A: 0, B: 0, C: 0, D: 0, E: 0 });
    setError(null);
    setSuccessMessage(`"${selectedConnectivity}" 연결성이 추가 대기 목록에 추가되었습니다.`);
  }, [selectedConnectivity, connectivityStorages, newConnectivityPrices, newConnectivities]);
  
  // 🔌 새 연결성 제거 (추가 대기 목록에서)
  const handleRemoveNewConnectivity = useCallback((connectivity: string) => {
    setNewConnectivities(prev => prev.filter(nc => nc.connectivity !== connectivity));
  }, []);
  
  // 📊 개별 조합 삭제/복원 토글
  // 🔥 버그 수정: 구분자를 '||'로 변경 (연결성에 '-'가 포함될 수 있음)
  const handleToggleVariantDelete = useCallback((storage: string, connectivity: string) => {
    const key = `${storage}||${connectivity}`;
    setVariantsToDelete(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  }, []);

  // 📊 변경사항 계산
  const hasChanges = useMemo(() => {
    return newStorages.length > 0 || storagesToDelete.length > 0 || 
           newConnectivities.length > 0 || variantsToDelete.size > 0;
  }, [newStorages, storagesToDelete, newConnectivities, variantsToDelete]);

  // 💾 저장 핸들러 (확장: 연결성 추가 및 개별 조합 삭제 지원)
  const handleSave = useCallback(async () => {
    if (!model) return;
    
    // 변경사항 없으면 그냥 닫기
    if (!hasChanges) {
      onClose();
      return;
    }

    // 🔥 검증 로직 개선: 용량 전체 삭제 vs 개별 조합 삭제 구분
    // 1. 용량 전체 삭제 시 검증 (storagesToDelete가 있을 때만)
    if (storagesToDelete.length > 0) {
      const allStorages = model.storageOptions || [];
      const remainingStorages = allStorages.filter(s => !storagesToDelete.includes(s));
      const totalStorages = remainingStorages.length + newStorages.length;
      if (totalStorages === 0) {
        setError("최소 1개의 용량 옵션이 필요합니다.");
        return;
      }
    }
    
    // 2. 개별 조합 삭제 시 검증: 최소 1개의 활성 조합이 남아있어야 함
    if (variantsToDelete.size > 0) {
      const allStorages = model.storageOptions || [];
      const allConnectivities = model.connectivityOptions || ['Wi-Fi'];
      const existingDisabled = (model as any).disabledVariants || [];
      
      // 모든 조합 계산
      const totalCombinations = allStorages.length * allConnectivities.length;
      
      // 기존 비활성 조합 수
      const existingDisabledCount = existingDisabled.length;
      
      // 새로 삭제 예정인 조합 수 (기존에 없는 것만)
      let newDisabledCount = 0;
      variantsToDelete.forEach(key => {
        const parts = key.split('||');
        if (parts.length === 2) {
          const [storage, connectivity] = parts;
          const alreadyDisabled = existingDisabled.some(
            (v: any) => v.storage === storage && (v.connectivity || 'Wi-Fi') === connectivity
          );
          if (!alreadyDisabled) {
            newDisabledCount++;
          }
        }
      });
      
      // 활성 조합 수 = 전체 - 기존 비활성 - 새로 비활성
      const remainingActive = totalCombinations - existingDisabledCount - newDisabledCount;
      
      if (remainingActive <= 0) {
        setError("최소 1개의 활성 조합이 필요합니다. 모든 조합을 삭제할 수 없습니다.");
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);

      const promises: Promise<any>[] = [];
      const UnifiedPriceService = getUnifiedPriceService();

      // 📊 새 storageOptions 배열 계산
      // 🔥 버그 수정: Set을 사용하여 중복 용량 제거
      const rawStorageOptions = [
        ...model.storageOptions.filter(s => !storagesToDelete.includes(s)),
        ...newStorages.map(ns => ns.storage)
      ];
      const newStorageOptionsArray = Array.from(new Set(rawStorageOptions));
      
      // 🔥 중복 제거 로그 (디버깅용)
      if (rawStorageOptions.length !== newStorageOptionsArray.length) {
        console.warn(`⚠️ [${model.modelKo}] storageOptions 중복 정리: ${rawStorageOptions.length}개 → ${newStorageOptionsArray.length}개`);
      }
      
      // 🔌 새 connectivityOptions 배열 계산
      // 🔥 버그 수정: Set을 사용하여 중복 연결성 제거
      const existingConnectivities = model.connectivityOptions || ['Wi-Fi'];
      const rawConnectivityOptions = [
        ...existingConnectivities,
        ...newConnectivities.map(nc => nc.connectivity)
      ];
      const newConnectivityOptionsArray = Array.from(new Set(rawConnectivityOptions));
      
      // 🔥 중복 제거 로그 (디버깅용)
      if (rawConnectivityOptions.length !== newConnectivityOptionsArray.length) {
        console.warn(`⚠️ [${model.modelKo}] connectivityOptions 중복 정리: ${rawConnectivityOptions.length}개 → ${newConnectivityOptionsArray.length}개`);
      }

      // 📊 disabledVariants 업데이트 계산
      const existingDisabledVariants: { storage: string; connectivity?: ConnectivityType }[] = 
        (model as any).disabledVariants || [];
      const addedStorages = newStorages.map(ns => ns.storage);
      const addedConnectivities = newConnectivities.map(nc => nc.connectivity);

      // 새로 추가되는 용량/연결성 조합은 disabledVariants에서 제거 (복원 효과)
      let filteredDisabledVariants = existingDisabledVariants.filter(
        (v) => !addedStorages.includes(v.storage)
      );

      // 삭제 예정 용량 추가 (단, 새로 추가되는 용량은 제외)
      const storagesToActuallyDelete = storagesToDelete.filter(s => !addedStorages.includes(s));
      
      // 🔌 개별 조합 삭제 처리 (variantsToDelete에서)
      // 🔥 버그 수정: '||' 구분자로 파싱 (연결성에 '-'가 포함될 수 있음)
      const individualVariantsToDisable: { storage: string; connectivity: ConnectivityType }[] = [];
      variantsToDelete.forEach(key => {
        const parts = key.split('||');
        if (parts.length === 2) {
          const [storage, connectivity] = parts;
          if (storage && connectivity) {
            individualVariantsToDisable.push({ storage, connectivity: connectivity as ConnectivityType });
          }
        }
      });

      const newDisabledVariants = [
        ...filteredDisabledVariants,
        // 용량 전체 삭제
        ...storagesToActuallyDelete.flatMap(storage => 
          newConnectivityOptionsArray.map(connectivity => ({
            storage,
            connectivity: connectivity as ConnectivityType
          }))
        ),
        // 개별 조합 삭제
        ...individualVariantsToDisable
      ];
      
      // 중복 제거
      const uniqueDisabledVariants = newDisabledVariants.filter((v, i, arr) => 
        arr.findIndex(x => x.storage === v.storage && x.connectivity === v.connectivity) === i
      );

      console.log(`🔧 용량/연결성 관리 저장:`, {
        기존용량: model.storageOptions,
        기존연결성: existingConnectivities,
        새용량: addedStorages,
        새연결성: addedConnectivities,
        삭제예정용량: storagesToActuallyDelete,
        개별삭제조합_keys: Array.from(variantsToDelete),
        개별삭제조합_파싱결과: individualVariantsToDisable,
        최종용량: newStorageOptionsArray,
        최종연결성: newConnectivityOptionsArray,
        비활성조합수: uniqueDisabledVariants.length,
        비활성조합상세: uniqueDisabledVariants
      });

      // 1️⃣ 모델 문서 업데이트
      const modelRef = doc(db, 'device_catalog', model.id);
      promises.push(
        updateDoc(modelRef, {
          storageOptions: newStorageOptionsArray,
          connectivityOptions: newConnectivityOptionsArray,
          disabledVariants: uniqueDisabledVariants,
          updatedAt: new Date(),
          updatedBy: 'admin'
        })
      );

      // 2️⃣ 새 용량에 대한 가격 문서 생성 (기존 연결성 + 새 연결성 모두)
      if (newStorages.length > 0) {
        for (const newStorage of newStorages) {
          for (const connectivity of newConnectivityOptionsArray) {
            // priceId 생성
            const effectiveConnectivity = (deviceType === 'wearable' || connectivity === 'Wi-Fi' || connectivity === '기본')
              ? undefined
              : connectivity;
            
            const priceId = generatePriceId(
              model.id,
              { 
                storage: newStorage.storage,
                connectivity: effectiveConnectivity
              },
              'KR'
            );

            console.log(`💰 새 용량 가격 문서 생성: ${priceId}`);

            promises.push(
              UnifiedPriceService.updatePrice({
                priceId,
                gradePrices: newStorage.initialPrices,
                memo: `용량 관리에서 추가 (${newStorage.storage} ${connectivity}) - ${new Date().toLocaleString()}`
              }, 'admin')
            );
          }
        }
      }
      
      // 3️⃣ 새 연결성에 대한 가격 문서 생성 (선택된 용량들에 대해)
      if (newConnectivities.length > 0) {
        for (const newConn of newConnectivities) {
          for (const storage of newConn.storages) {
            const effectiveConnectivity = (deviceType === 'wearable' || newConn.connectivity === 'Wi-Fi' || newConn.connectivity === '기본')
              ? undefined
              : newConn.connectivity;
            
            const priceId = generatePriceId(
              model.id,
              { 
                storage: storage,
                connectivity: effectiveConnectivity
              },
              'KR'
            );

            console.log(`💰 새 연결성 가격 문서 생성: ${priceId}`);

            promises.push(
              UnifiedPriceService.updatePrice({
                priceId,
                gradePrices: newConn.initialPrices,
                memo: `연결성 관리에서 추가 (${storage} ${newConn.connectivity}) - ${new Date().toLocaleString()}`
              }, 'admin')
            );
          }
        }
      }

      // 4️⃣ 모든 변경사항 실행
      await Promise.all(promises);

      console.log(`✅ 용량/연결성 관리 저장 완료`);

      // 5️⃣ 캐시 정리 및 성공 처리
      DeviceCatalogService.clearCache();
      UnifiedPriceService.clearCache();
      
      onSuccess();
      onClose();

    } catch (err) {
      console.error("❌ 용량/연결성 관리 저장 실패:", err);
      setError(err instanceof Error ? err.message : "저장 중 알 수 없는 오류가 발생했습니다.");
    } finally {
      setLoading(false);
    }
  }, [model, hasChanges, activeStorageOptions, activeConnectivityOptions, storagesToDelete, newStorages, newConnectivities, variantsToDelete, deviceType, onSuccess, onClose]);

  // 🎨 가용 스토리지 옵션 (이미 존재하는 것 제외)
  const availableStorageOptions = useMemo(() => {
    const allExisting = [
      ...activeStorageOptions.filter(s => !storagesToDelete.includes(s)),
      ...newStorages.map(ns => ns.storage)
    ];
    return getDefaultStorageOptions().filter(opt => !allExisting.includes(opt));
  }, [activeStorageOptions, storagesToDelete, newStorages, deviceType]);

  if (!model) return null;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 500 },
          maxWidth: '100%'
        }
      }}
    >
      {/* 🔝 헤더 */}
      <Box
        sx={{
          p: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          bgcolor: 'primary.main',
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <StorageIcon />
          <Box>
            <Typography variant="h6" fontWeight={700}>
              용량/연결성 관리
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.9 }}>
              {model.modelKo}
            </Typography>
          </Box>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </Box>
      
      {/* 📊 탭 헤더 (태블릿에서만 연결성 탭 표시) */}
      {deviceType === 'tablet' && (
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: '#f8fafc' }}>
          <Tabs 
            value={activeTab} 
            onChange={(_, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
          >
            <Tab 
              label="용량 관리" 
              icon={<StorageIcon fontSize="small" />} 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab 
              label="연결성 추가" 
              icon={<CellularIcon fontSize="small" />} 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
            <Tab 
              label="조합 관리" 
              icon={<GridIcon fontSize="small" />} 
              iconPosition="start"
              sx={{ minHeight: 48 }}
            />
          </Tabs>
        </Box>
      )}

      {/* 📊 컨텐츠 */}
      <Box sx={{ p: 2, overflowY: 'auto', flex: 1 }}>
        {/* 에러/성공 메시지 */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {successMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
            {successMessage}
          </Alert>
        )}

        {/* 📋 모델 정보 요약 */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#f8fafc' }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>
            📱 모델 정보
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {model.modelEn} · {model.series}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            연결성 옵션: {model.connectivityOptions?.join(', ') || 'Wi-Fi'}
          </Typography>
        </Paper>

        {/* 📋 용량 관리 탭 (기본 또는 탭 0) */}
        {(deviceType !== 'tablet' || activeTab === 0) && (
        <>
        {/* 📋 현재 등록된 용량 */}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📋 현재 등록된 용량
          <Chip label={`${activeStorageOptions.length}개`} size="small" color="primary" />
        </Typography>
        
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          {activeStorageOptions.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              등록된 용량이 없습니다.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {activeStorageOptions.map((storage) => {
                const isMarkedForDelete = storagesToDelete.includes(storage);
                return (
                  <Chip
                    key={storage}
                    label={storage}
                    color={isMarkedForDelete ? 'error' : 'primary'}
                    variant={isMarkedForDelete ? 'outlined' : 'filled'}
                    sx={{
                      textDecoration: isMarkedForDelete ? 'line-through' : 'none',
                      opacity: isMarkedForDelete ? 0.6 : 1
                    }}
                    onDelete={() => handleToggleDeleteStorage(storage)}
                    deleteIcon={
                      <Tooltip title={isMarkedForDelete ? "삭제 취소 (복원)" : "삭제 예정에 추가"}>
                        {isMarkedForDelete ? <RestoreIcon /> : <DeleteIcon />}
                      </Tooltip>
                    }
                  />
                );
              })}
            </Box>
          )}
          
          {storagesToDelete.length > 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              <AlertTitle>삭제 예정: {storagesToDelete.length}개</AlertTitle>
              저장 시 {storagesToDelete.join(', ')} 용량의 가격 행이 숨겨집니다. (복원 가능)
            </Alert>
          )}
        </Paper>

        <Divider sx={{ my: 3 }} />

        {/* ➕ 새 용량 추가 */}
        <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          ➕ 새 용량 추가
        </Typography>
        
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            bgcolor: '#fffbeb',
            border: '1px dashed #f59e0b'
          }}
        >
          {/* ☑️ 체크박스로 여러 용량 선택 */}
          {availableStorageOptions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                추가할 용량 선택 (여러 개 선택 가능)
              </Typography>
              <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
                <FormGroup row sx={{ gap: 0.5 }}>
                  {availableStorageOptions.map((opt) => (
                    <FormControlLabel
                      key={opt}
                      control={
                        <Checkbox
                          checked={selectedStorages.has(opt)}
                          onChange={(e) => handleStorageCheckChange(opt, e.target.checked)}
                          size="small"
                          color="success"
                        />
                      }
                      label={
                        <Chip 
                          label={opt} 
                          size="small" 
                          variant={selectedStorages.has(opt) ? 'filled' : 'outlined'}
                          color={selectedStorages.has(opt) ? 'success' : 'default'}
                          sx={{ cursor: 'pointer' }}
                        />
                      }
                      sx={{ 
                        m: 0, 
                        mr: 1, 
                        mb: 0.5,
                        '& .MuiFormControlLabel-label': { ml: -0.5 }
                      }}
                    />
                  ))}
                </FormGroup>
              </Paper>
              {selectedStorages.size > 0 && (
                <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
                  ✓ {selectedStorages.size}개 선택됨: {Array.from(selectedStorages).join(', ')}
                </Typography>
              )}
            </Box>
          )}

          {/* 직접 입력 */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
              또는 직접 입력
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                placeholder="예: 2TB"
                value={customStorageInput}
                onChange={(e) => setCustomStorageInput(e.target.value)}
                sx={{ flex: 1 }}
              />
              <Button
                variant="outlined"
                color="success"
                onClick={handleAddCustomStorage}
                disabled={!customStorageInput.trim()}
                sx={{ minWidth: 80 }}
              >
                추가
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* 초기 가격 설정 */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
              초기 가격 (선택사항 - 나중에 설정 가능)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {(getGradesForCategory(deviceType) as DeviceGrade[]).map((grade) => (
                <TextField
                  key={grade}
                  size="small"
                  label={getGradeLabel(grade)}
                  value={newStoragePrices[grade] || ''}
                  onChange={(e) => {
                    const value = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
                    setNewStoragePrices(prev => ({ ...prev, [grade]: value }));
                  }}
                  sx={{ width: 90 }}
                  InputProps={{
                    sx: { fontSize: '0.8rem' }
                  }}
                />
              ))}
            </Box>
          </Box>

          {/* 선택된 용량 일괄 추가 버튼 */}
          {selectedStorages.size > 0 && (
            <Button
              fullWidth
              variant="contained"
              color="success"
              startIcon={<AddIcon />}
              onClick={handleAddSelectedStorages}
              sx={{ mb: 1 }}
            >
              선택한 {selectedStorages.size}개 용량 추가
            </Button>
          )}

          {/* 추가 대기 목록 */}
          {newStorages.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="success.main" fontWeight={600}>
                ✅ 추가 대기 목록 ({newStorages.length}개)
              </Typography>
              <List dense sx={{ bgcolor: '#f0fdf4', borderRadius: 1, mt: 1 }}>
                {newStorages.map((ns, index) => (
                  <ListItem key={ns.storage} divider={index < newStorages.length - 1}>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Chip label={ns.storage} color="success" size="small" />
                          <Typography variant="caption" color="text.secondary">
                            A: {ns.initialPrices.A.toLocaleString()}원 / 
                            B: {ns.initialPrices.B.toLocaleString()}원
                          </Typography>
                        </Box>
                      }
                    />
                    <ListItemSecondaryAction>
                      <IconButton 
                        edge="end" 
                        size="small"
                        onClick={() => handleRemoveNewStorage(ns.storage)}
                        color="error"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Paper>

        {/* 💡 안내 (용량 탭) */}
        <Alert severity="info" icon={<InfoIcon />}>
          <AlertTitle>💡 용량 추가 안내</AlertTitle>
          <Typography variant="body2">
            용량 추가 시 각 연결성 옵션({model.connectivityOptions?.join(', ') || 'Wi-Fi'})에 대해 
            자동으로 가격 행이 생성됩니다.
          </Typography>
        </Alert>
        </>
        )}
        
        {/* 🔌 연결성 추가 탭 (태블릿 전용) */}
        {deviceType === 'tablet' && activeTab === 1 && (
          <Box>
            {/* 현재 연결성 목록 */}
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              🔌 현재 등록된 연결성
              <Chip label={`${activeConnectivityOptions.length}개`} size="small" color="primary" />
            </Typography>
            
            <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {activeConnectivityOptions.map(connectivity => (
                  <Chip
                    key={connectivity}
                    label={connectivity}
                    color="primary"
                    icon={getConnectivityIcon(connectivity)}
                  />
                ))}
              </Box>
            </Paper>
            
            <Divider sx={{ my: 3 }} />
            
            {/* 새 연결성 추가 */}
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              ➕ 새 연결성 추가
            </Typography>
            
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                mb: 3,
                bgcolor: '#eff6ff',
                border: '1px dashed #3b82f6'
              }}
            >
              {availableConnectivityOptions.length === 0 ? (
                <Alert severity="info">
                  모든 연결성 옵션이 이미 등록되어 있습니다.
                </Alert>
              ) : (
                <>
                  {/* 연결성 선택 */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                      추가할 연결성 선택
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      {availableConnectivityOptions.map(opt => (
                        <Chip
                          key={opt}
                          label={opt}
                          icon={getConnectivityIcon(opt)}
                          onClick={() => setSelectedConnectivity(opt)}
                          color={selectedConnectivity === opt ? 'primary' : 'default'}
                          variant={selectedConnectivity === opt ? 'filled' : 'outlined'}
                          sx={{ cursor: 'pointer' }}
                        />
                      ))}
                    </Box>
                  </Box>
                  
                  {selectedConnectivity && (
                    <>
                      {/* 적용할 용량 선택 */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                          "{selectedConnectivity}" 연결성을 추가할 용량 선택
                        </Typography>
                        <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'white' }}>
                          <FormGroup row sx={{ gap: 0.5 }}>
                            {activeStorageOptions.map(storage => (
                              <FormControlLabel
                                key={storage}
                                control={
                                  <Checkbox
                                    checked={connectivityStorages.has(storage)}
                                    onChange={(e) => handleConnectivityStorageToggle(storage, e.target.checked)}
                                    size="small"
                                    color="primary"
                                  />
                                }
                                label={
                                  <Chip 
                                    label={storage} 
                                    size="small" 
                                    variant={connectivityStorages.has(storage) ? 'filled' : 'outlined'}
                                    color={connectivityStorages.has(storage) ? 'primary' : 'default'}
                                  />
                                }
                                sx={{ m: 0, mr: 1, mb: 0.5 }}
                              />
                            ))}
                          </FormGroup>
                        </Paper>
                        {connectivityStorages.size > 0 && (
                          <Typography variant="caption" color="primary.main" sx={{ mt: 1, display: 'block' }}>
                            ✓ {connectivityStorages.size}개 용량 선택됨
                          </Typography>
                        )}
                      </Box>
                      
                      {/* 초기 가격 설정 */}
                      <Box sx={{ mb: 2 }}>
                        <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
                          초기 가격 (선택사항)
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {(getGradesForCategory(deviceType) as DeviceGrade[]).map((grade) => (
                            <TextField
                              key={grade}
                              size="small"
                              label={getGradeLabel(grade)}
                              value={newConnectivityPrices[grade] || ''}
                              onChange={(e) => {
                                const value = parseInt(e.target.value.replace(/[^0-9]/g, '')) || 0;
                                setNewConnectivityPrices(prev => ({ ...prev, [grade]: value }));
                              }}
                              sx={{ width: 90 }}
                            />
                          ))}
                        </Box>
                      </Box>
                      
                      {/* 추가 버튼 */}
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddConnectivity}
                        disabled={connectivityStorages.size === 0}
                      >
                        {selectedConnectivity} 연결성 추가
                      </Button>
                    </>
                  )}
                </>
              )}
              
              {/* 추가 대기 목록 */}
              {newConnectivities.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="primary.main" fontWeight={600}>
                    ✅ 추가 대기 목록 ({newConnectivities.length}개 연결성)
                  </Typography>
                  <List dense sx={{ bgcolor: '#dbeafe', borderRadius: 1, mt: 1 }}>
                    {newConnectivities.map((nc, index) => (
                      <ListItem key={nc.connectivity} divider={index < newConnectivities.length - 1}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {getConnectivityIcon(nc.connectivity)}
                              <Chip label={nc.connectivity} color="primary" size="small" />
                            </Box>
                          }
                          secondary={`적용 용량: ${nc.storages.join(', ')}`}
                        />
                        <ListItemSecondaryAction>
                          <IconButton 
                            edge="end" 
                            size="small"
                            onClick={() => handleRemoveNewConnectivity(nc.connectivity)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
            </Paper>
            
            {/* 안내 */}
            <Alert severity="info" icon={<InfoIcon />}>
              <AlertTitle>💡 연결성 추가 안내</AlertTitle>
              <Typography variant="body2">
                새 연결성(예: Wi-Fi + Cellular)을 추가하면 선택한 용량에 대해 새 가격 행이 생성됩니다.<br/>
                예: 128GB Wi-Fi만 있는 모델에 Wi-Fi+Cellular를 추가하면 128GB Wi-Fi+Cellular 가격 행 생성
              </Typography>
            </Alert>
          </Box>
        )}
        
        {/* 📊 조합 관리 탭 (태블릿 전용) */}
        {deviceType === 'tablet' && activeTab === 2 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              📊 용량×연결성 조합 관리
              <Chip 
                label={`${activeVariantCombinations.filter(v => v.isActive).length}개 활성`} 
                size="small" 
                color="success" 
              />
            </Typography>
            
            <Paper variant="outlined" sx={{ mb: 3 }}>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>용량</TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>연결성</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>상태</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, bgcolor: '#f8fafc' }}>작업</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeVariantCombinations.map((combo) => {
                      // 🔥 버그 수정: '||' 구분자 사용
                      const key = `${combo.storage}||${combo.connectivity}`;
                      const isMarkedForDelete = variantsToDelete.has(key);
                      const effectivelyActive = combo.isActive && !isMarkedForDelete;
                      
                      return (
                        <TableRow 
                          key={key}
                          sx={{ 
                            bgcolor: isMarkedForDelete ? '#fef2f2' : (combo.isActive ? 'white' : '#f9fafb'),
                            opacity: effectivelyActive ? 1 : 0.6
                          }}
                        >
                          <TableCell>
                            <Chip label={combo.storage} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              {getConnectivityIcon(combo.connectivity)}
                              <Typography variant="body2">{combo.connectivity}</Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {isMarkedForDelete ? (
                              <Chip label="삭제 예정" size="small" color="error" variant="outlined" />
                            ) : combo.isActive ? (
                              <Chip label="활성" size="small" color="success" />
                            ) : (
                              <Chip label="비활성" size="small" color="default" variant="outlined" />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {combo.isActive && (
                              <Tooltip title={isMarkedForDelete ? "삭제 취소 (복원)" : "삭제 예정에 추가"}>
                                <IconButton 
                                  size="small"
                                  color={isMarkedForDelete ? "success" : "error"}
                                  onClick={() => handleToggleVariantDelete(combo.storage, combo.connectivity)}
                                >
                                  {isMarkedForDelete ? <RestoreIcon fontSize="small" /> : <DeleteIcon fontSize="small" />}
                                </IconButton>
                              </Tooltip>
                            )}
                            {!combo.isActive && (
                              <Typography variant="caption" color="text.secondary">-</Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
            
            {variantsToDelete.size > 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                <AlertTitle>삭제 예정: {variantsToDelete.size}개 조합</AlertTitle>
                저장 시 해당 조합의 가격 행이 숨겨집니다. (복원 가능)
              </Alert>
            )}
            
            {/* 안내 */}
            <Alert severity="info" icon={<InfoIcon />}>
              <AlertTitle>💡 조합 관리 안내</AlertTitle>
              <Typography variant="body2">
                개별 용량×연결성 조합을 비활성화하면 해당 가격 행이 숨겨집니다.<br/>
                예: 128GB Wi-Fi는 유지하고 128GB Wi-Fi+Cellular만 비활성화 가능
              </Typography>
            </Alert>
          </Box>
        )}
      </Box>

      {/* 🔽 푸터: 저장 버튼 */}
      <Box
        sx={{
          p: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: '#f8fafc',
          display: 'flex',
          gap: 2
        }}
      >
        <Button
          variant="outlined"
          onClick={onClose}
          disabled={loading}
          sx={{ flex: 1 }}
        >
          취소
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={loading || !hasChanges}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
          sx={{ flex: 1 }}
        >
          {loading ? '저장 중...' : hasChanges 
            ? `저장 (${newStorages.length + storagesToDelete.length + newConnectivities.length + variantsToDelete.size}개 변경)` 
            : '변경사항 없음'}
        </Button>
      </Box>
    </Drawer>
  );
};

// 🔧 displayName 설정
VariantManagerDrawer.displayName = 'VariantManagerDrawer';

export default VariantManagerDrawer;
